import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, PackageCheck, Ban, Plus } from "lucide-react";

const STATUS_BADGE = {
  in_transit: "bg-secondary/15 text-secondary",
  received: "bg-success/10 text-success",
  cancelled: "bg-muted text-muted-foreground",
};

export default function Transfers() {
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();
  const { data: transfers = [] } = useQuery({
    queryKey: ["transfers"],
    queryFn: async () => (await api.get("/transfers")).data,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transfers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="data-label">Between godowns</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Transfers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send stock to another godown. It stays “in transit” until someone there confirms it arrived.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(v => !v)} data-testid="transfer-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New transfer
        </button>
      </div>

      {showNew && <NewTransfer onDone={() => { setShowNew(false); qc.invalidateQueries(); }} />}

      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="transfers-list">
        {transfers.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No transfers yet.</div>
        )}
        {transfers.map(t => <TransferRow key={t.id} t={t} />)}
      </div>
    </div>
  );
}

function NewTransfer({ onDone }) {
  const { data: containers = [] } = useQuery({
    queryKey: ["containers-list"],
    queryFn: async () => (await api.get("/containers")).data,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data.filter(l => l.active),
  });
  const godowns = locations.filter(l => !l.parent_id);
  const locName = Object.fromEntries(locations.map(l => [l.id, l.name]));

  const [selected, setSelected] = useState({});
  const [toLoc, setToLoc] = useState("");
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = containers.filter(c =>
    !filter || c.code.toLowerCase().includes(filter.toLowerCase())
    || (c.description || "").toLowerCase().includes(filter.toLowerCase())
  );
  const ids = Object.keys(selected).filter(k => selected[k]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/transfers", { container_ids: ids, to_location_id: toLoc, note });
      toast.success(`Transfer ${data.number} dispatched`);
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-4" data-testid="transfer-new-form">
      <input className="field-sm" placeholder="Filter containers by code or description…"
        value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="transfer-filter" />
      <div className="max-h-72 overflow-auto border border-border rounded-sm">
        <ul className="divide-y divide-border">
          {filtered.map(c => (
            <li key={c.id} className="flex items-center gap-3 p-3">
              <input type="checkbox" checked={!!selected[c.id]}
                data-testid={`transfer-select-${c.id}`}
                onChange={(e) => setSelected(s => ({ ...s, [c.id]: e.target.checked }))} />
              <div className="flex-1 min-w-0">
                <div className="container-code">{c.code}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {c.description} · at {locName[c.location_id] || "?"}
                </div>
              </div>
              <div className="font-mono text-sm">{c.current_qty}</div>
            </li>
          ))}
          {!filtered.length && <li className="p-4 text-sm text-muted-foreground">No containers in stock.</li>}
        </ul>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <select className="field" value={toLoc} onChange={(e) => setToLoc(e.target.value)}
                data-testid="transfer-to-godown">
          <option value="">Send to which godown?</option>
          {godowns.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
        </select>
        <input className="field" placeholder="Note (optional — e.g. truck no., driver)"
               value={note} onChange={(e) => setNote(e.target.value)} data-testid="transfer-note" />
      </div>
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{ids.length} container{ids.length === 1 ? "" : "s"} selected</div>
        <button onClick={submit} disabled={busy || !ids.length || !toLoc}
                className="btn-primary" data-testid="transfer-submit">
          <Truck className="w-4 h-4 mr-1" /> {busy ? "Dispatching…" : "Dispatch"}
        </button>
      </div>
    </div>
  );
}

export function TransferRow({ t, receiverMode = false }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const totalQty = t.lines.reduce((s, l) => s + l.qty, 0);

  return (
    <div className="p-4" data-testid={`transfer-row-${t.number}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`badge-flat ${STATUS_BADGE[t.status]}`}>{t.status.replace("_", " ")}</span>
        <button onClick={() => setOpen(v => !v)} className="font-mono text-sm font-semibold text-primary">
          {t.number}
        </button>
        <div className="flex-1 min-w-0 text-sm truncate">
          {t.lines.length} container{t.lines.length === 1 ? "" : "s"} · {totalQty.toLocaleString()} total
          → <span className="font-medium">{t.to_location_name}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {new Date(t.dispatched_at).toLocaleString()} by {t.dispatched_by_name}
        </div>
        {t.status === "in_transit" && <ReceiveButton t={t} onDone={() => qc.invalidateQueries()} />}
        {t.status === "in_transit" && !receiverMode && <CancelButton t={t} onDone={() => qc.invalidateQueries()} />}
      </div>
      {t.status === "received" && (
        <div className="mt-1 text-xs text-success">
          Received by {t.received_by_name} · kept at {t.received_location_name}
          {t.receive_note ? ` · ${t.receive_note}` : ""}
        </div>
      )}
      {t.note && <div className="mt-1 text-xs text-muted-foreground">Note: {t.note}</div>}
      {open && (
        <ul className="mt-2 space-y-1 text-xs border-l-2 border-primary/10 pl-3">
          {t.lines.map(l => (
            <li key={l.container_id} className="flex items-center gap-2">
              <span className="container-code text-primary">{l.code}</span>
              <span className="truncate">{l.item_name}</span>
              <span className="text-muted-foreground">from {l.from_location_name}</span>
              <span className="ml-auto font-mono font-semibold">{l.qty}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReceiveButton({ t, onDone }) {
  const [show, setShow] = useState(false);
  const [locId, setLocId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: locations = [] } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data.filter(l => l.active),
    enabled: show,
  });

  // only spots inside the destination godown (or the godown itself)
  const byId = Object.fromEntries(locations.map(l => [l.id, l]));
  const inDest = locations.filter(l => {
    let cur = l;
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
      if (cur.id === t.to_location_id) return true;
      seen.add(cur.id);
      cur = byId[cur.parent_id];
    }
    return false;
  });

  const receive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/transfers/${t.id}/receive`, { location_id: locId, note });
      toast.success(`Received — kept at ${data.kept_at}`);
      setShow(false);
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!show) {
    return (
      <button className="btn-primary text-xs" onClick={() => setShow(true)}
              data-testid={`transfer-receive-${t.number}`}>
        <PackageCheck className="w-4 h-4 mr-1" /> Mark received
      </button>
    );
  }
  return (
    <div className="w-full mt-2 flex gap-2 flex-wrap items-center bg-muted/40 border border-border rounded-sm p-3">
      <select className="field-sm flex-1 min-w-[180px]" value={locId}
              onChange={(e) => setLocId(e.target.value)} data-testid={`transfer-receive-loc-${t.number}`}>
        <option value="">Where did you keep it?</option>
        {inDest.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
      </select>
      <input className="field-sm flex-1 min-w-[140px]" placeholder="Note (optional)"
             value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="btn-primary text-xs" disabled={busy || !locId} onClick={receive}
              data-testid={`transfer-receive-confirm-${t.number}`}>
        {busy ? "Saving…" : "Confirm"}
      </button>
      <button className="btn-ghost text-xs" onClick={() => setShow(false)}>Cancel</button>
    </div>
  );
}

function CancelButton({ t, onDone }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancel ${t.number}? Containers go back to where they were.`)) return;
    setBusy(true);
    try {
      await api.post(`/transfers/${t.id}/cancel`);
      toast.success("Transfer cancelled");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <button className="btn-ghost text-xs text-destructive" disabled={busy} onClick={cancel}
            data-testid={`transfer-cancel-${t.number}`}>
      <Ban className="w-4 h-4 mr-1" /> Cancel
    </button>
  );
}
